import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import ExportOutlined from '@ant-design/icons/ExportOutlined';
import { useQueryClient } from '@tanstack/react-query';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Checkbox from 'antd/es/checkbox';
import Divider from 'antd/es/divider';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Typography from 'antd/es/typography';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { WhoamiResponseSchema } from '@mai/types';

import {
  CardContainer,
  ChangeLink,
  LoginMethodContainer,
  LoginOptionsContainer,
  MagicLinkSection,
  PasswordSection,
  SubText,
} from './styles';

import UnauthCard from '@components/UnauthCard';
import { apiClient } from '@queries/axios';
import { useSessionUserId } from '@utils/hooks';
import { track } from '@utils/mixpanel';

type FormData = {
  email: string;
  password: string;
};

const LoginPage = () => {
  const { updateUserId } = useSessionUserId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormData>();
  const [isLoading, setIsLoading] = useState(false);
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const email = searchParams.get('email');
  const magicLinkSentAt = useMemo(() => {
    const rawMagicLinkSentAt = searchParams.get('magicLinkSentAt');
    if (!rawMagicLinkSentAt) return null;
    return new Date(rawMagicLinkSentAt);
  }, [searchParams]);
  const formEmailValue = Form.useWatch('email', form);
  const isContinueDisabled = !formEmailValue?.length;
  const [secondsBeforeResend, setSecondsBeforeResend] = useState(0);
  const isDevMode = import.meta.env.MODE === 'development';

  useEffect(() => {
    if (!magicLinkSentAt) return;

    const calculateInitialSeconds = () => {
      const elapsedMs = new Date().getTime() - magicLinkSentAt.getTime();
      const remainingSeconds = Math.max(30 - Math.floor(elapsedMs / 1000), 0);
      return remainingSeconds;
    };

    setSecondsBeforeResend(calculateInitialSeconds());

    const intervalId = setInterval(() => {
      setSecondsBeforeResend((prev) => {
        if (prev <= 0) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [magicLinkSentAt]);

  const onEmailSubmit = async ({ email }: Pick<FormData, 'email'>) => {
    setSearchParams({ email });
    form.resetFields();
  };

  const onBack = () => {
    setSearchParams({});
  };

  const onPasswordLogin = async ({ password }: Pick<FormData, 'password'>) => {
    if (!email) return;

    setIsLoading(true);
    try {
      await apiClient.post('/authentication/login', {
        email,
        password,
      });

      const whoamiResponse = await apiClient.get('/authentication/whoami');
      const whoamiData = WhoamiResponseSchema.parse(whoamiResponse.data);
      updateUserId(whoamiData.userId);

      void message.success('Login successful');
      track('LOGGED_IN', {
        email,
        loginMethod: 'password',
      });
      await queryClient.clear();
      navigate('/');
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          void message.error(
            'Invalid email or password, please check and try again.',
          );
        } else if (error.response?.status === 429) {
          void message.error('Too many login attempts, please try again later');
        } else {
          void message.error('An error occurred. Please try again.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onMagicLinkLogin = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      await apiClient.post('/authentication/email/initialize', {
        email,
      });
      setSearchParams((prev) => {
        prev.set('magicLinkSentAt', new Date().toISOString());
        return prev;
      });
      track('MAGIC_LINK_REQUESTED', {
        email,
        loginMethod: 'magic-link',
      });
      void message.success('A link has been sent to your email');
    } catch (error) {
      void message.error('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderEmailInput = () => (
    <LoginMethodContainer>
      <Typography.Title level={3} style={{ marginBottom: '24px' }}>
        Sign in to your account
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={onEmailSubmit}
        initialValues={{ email }}
      >
        <Form.Item
          name="email"
          label={
            <Typography.Text strong>
              What is your sign-in email address?
            </Typography.Text>
          }
          rules={[
            {
              required: true,
              message: 'Please input a valid email address',
              pattern: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/g,
            },
          ]}
          style={{ textAlign: 'left' }}
        >
          <Input size="large" placeholder="hello@example.com" />
        </Form.Item>
        <Form.Item>
          <Button
            loading={isLoading}
            type="primary"
            htmlType="submit"
            style={{ width: '100%' }}
            size="large"
            disabled={isContinueDisabled}
          >
            Continue <ArrowRightOutlined />
          </Button>
        </Form.Item>
      </Form>

      <Divider style={{ margin: '24px 0' }} />

      <Typography.Text type="secondary" style={{ marginBottom: '16px' }}>
        Don't have an account yet?
      </Typography.Text>

      <Link
        to="https://docs.google.com/forms/d/e/1FAIpQLSfyhebZAsj62wI6EXSb53zEQs0wRANr2Hx2q15gMmwOZV-fjQ/viewform"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button
          style={{ width: '100%' }}
          size="large"
          icon={<ExportOutlined />}
        >
          Request an invite
        </Button>
      </Link>
    </LoginMethodContainer>
  );

  const renderLoginOptions = () => (
    <LoginOptionsContainer>
      <Typography.Title level={3} style={{ marginBottom: '4px' }}>
        Sign in to your account
      </Typography.Title>
      <SubText>
        Login as <b>{email}</b> (
        <ChangeLink onClick={onBack}>change</ChangeLink>)
      </SubText>

      {isDevMode && (
        <PasswordSection>
          <Form form={form} layout="vertical" onFinish={onPasswordLogin}>
            <Form.Item
              name="password"
              label="What is your password?"
              required
              style={{ marginBottom: '16px', textAlign: 'left' }}
              rules={[
                {
                  required: true,
                  message: 'Please input your password',
                },
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
            <Button
              loading={isLoading}
              type="primary"
              htmlType="submit"
              style={{ width: '100%' }}
              size="large"
            >
              Sign In
            </Button>
          </Form>

          <div
            style={{
              display: 'flex',
              gap: '16px',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Checkbox>Remember me</Checkbox>
            <Link to="/auth/reset-password">Forgot password?</Link>
          </div>
        </PasswordSection>
      )}

      {isDevMode ? (
        <>
          <Divider>
            <Typography.Text type="secondary">or</Typography.Text>
          </Divider>

          <MagicLinkSection>
            <Typography.Text>Long password? Hard to type?</Typography.Text>
            <Typography.Text type="secondary">
              We can email you a link so you can sign in without having to type
              your password.
            </Typography.Text>
            <Button
              size="large"
              onClick={() => {
                void onMagicLinkLogin();
              }}
            >
              Email Me A Link
            </Button>
          </MagicLinkSection>
        </>
      ) : (
        <MagicLinkSection>
          <Typography.Text>
            We'll send you a secure link to sign in to your account.
          </Typography.Text>
          <Typography.Text type="secondary">
            Click the link in the email to sign in without having to type your
            password.
          </Typography.Text>
          <Button
            size="large"
            type="primary"
            onClick={() => {
              void onMagicLinkLogin();
            }}
          >
            Email Me A Sign-in Link
          </Button>
        </MagicLinkSection>
      )}
    </LoginOptionsContainer>
  );

  const renderMagicLinkLogin = () => {
    return (
      <LoginMethodContainer>
        <Typography.Title level={3}>Check your email</Typography.Title>
        <Typography.Text>
          We sent a link to <b>{email}</b>. Please click the link in the email
          to sign in.
        </Typography.Text>
        <Divider />
        <Typography.Text style={{ marginBottom: '16px' }}>
          Didn't receive the email?{' '}
          {secondsBeforeResend > 0
            ? `Please wait 30 seconds before you can resend the email.`
            : ''}
        </Typography.Text>
        <Button
          disabled={secondsBeforeResend > 0}
          onClick={() => {
            void onMagicLinkLogin();
          }}
        >
          Send link again{' '}
          {secondsBeforeResend > 0 ? `(${secondsBeforeResend} seconds)` : ''}
        </Button>
      </LoginMethodContainer>
    );
  };

  if (!email) {
    return (
      <CardContainer>
        <UnauthCard>{renderEmailInput()}</UnauthCard>
      </CardContainer>
    );
  }

  if (magicLinkSentAt) {
    return (
      <CardContainer>
        <UnauthCard>{renderMagicLinkLogin()}</UnauthCard>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <UnauthCard>{renderLoginOptions()}</UnauthCard>
    </CardContainer>
  );
};

export default LoginPage;
